// src/api/index.js
import { getActiveTimezone } from "../utils/dateTime";
// Centralized API client – production hardened
// ------------------------------------------------------------

// FAIL FAST: Env vars in Vite are compile-time.
// If this throws, your build is misconfigured (GOOD).
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

if (!API_BASE_URL) {
  throw new Error(
    "VITE_API_BASE_URL is not defined. " +
    "Check your production build environment (.env, CI, CloudFront build step)."
  );
}

// Interactive Runner base URL (port 8001) — for heavy data/parquet queries
// Falls back to main API if not configured so nothing breaks
const INTERACTIVE_BASE_URL = import.meta.env.VITE_INTERACTIVE_BASE_URL || API_BASE_URL;


// Normalize (trim, remove trailing slash) and upgrade to HTTPS if needed
const cleanBase = API_BASE_URL.trim().replace(/\/+$/, '');
const NORMALIZED_API_BASE = (window.location.protocol === 'https:')
  ? cleanBase.replace(/^http:\/\//i, 'https://')
  : cleanBase;

// console.log("[DEBUG] API Config:", {
//   raw: API_BASE_URL,
//   cleaned: cleanBase,
//   final: NORMALIZED_API_BASE,
//   mode: import.meta.env.MODE
// });

// console.debug("[DEBUG-api] Initialized API Client.");

// ------------------------------------------------------------
// Token helpers
// ------------------------------------------------------------
export function getStoredToken() {
  // sessionStorage is per-tab: each system/tab has its own isolated token.
  return sessionStorage.getItem("access_token");
}

export function getRefreshToken() {
  return sessionStorage.getItem("refresh_token");
}

export function saveTokens(accessToken, refreshToken) {
  if (accessToken) {
    sessionStorage.setItem("access_token", accessToken);
  }
  if (refreshToken) {
    sessionStorage.setItem("refresh_token", refreshToken);
  }
  // Dispatch event so AuthContext state stays in sync within this tab
  window.dispatchEvent(new CustomEvent('auth-token-synced', {
    detail: { accessToken, refreshToken }
  }));
}

export function clearTokens() {
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("refresh_token");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("token");
  localStorage.removeItem("authToken");
  // Notify this tab's AuthContext
  window.dispatchEvent(new CustomEvent('auth-token-synced', {
    detail: { accessToken: null, refreshToken: null }
  }));
}

let refreshPromise = null;

export async function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${NORMALIZED_API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
        credentials: "include",
      });

      if (!res.ok) {
        clearTokens();
        window.dispatchEvent(new CustomEvent('session-expired'));
        return null;
      }

      const data = await res.json();
      if (data?.access_token) {
        saveTokens(data.access_token, data.refresh_token);
        return data.access_token;
      }
      clearTokens();
      window.dispatchEvent(new CustomEvent('session-expired'));
      return null;
    } catch (err) {
      console.warn("[API] Refresh failed:", err);
      clearTokens();
      window.dispatchEvent(new CustomEvent('session-expired'));
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ------------------------------------------------------------
// Core fetch wrapper (ALL requests go through here)
// ------------------------------------------------------------
export async function apiFetch(
  endpoint,
  token = null,
  options = {},
  retrying = false
) {
  let effectiveToken = token || getStoredToken();

  // Normalize endpoint: ensure leading slash
  let safeEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;

  const url = `${NORMALIZED_API_BASE}${safeEndpoint}`;
  const isFormData = options.body instanceof FormData;

  const headers = {
    ...(!isFormData && { "Content-Type": "application/json" }),
    ...(effectiveToken && { Authorization: `Bearer ${effectiveToken}` }),
    "X-Timezone": getActiveTimezone(),
    ...options.headers,
  };

  if (effectiveToken) {
    const tokenType = token ? "passed" : "stored";
    // console.debug(`[API] Authorizing with ${tokenType} token for ${safeEndpoint}`);
  } else {
    console.warn(`[API] No token available for ${safeEndpoint}`);
  }

  const config = {
    ...options,
    headers,
    credentials: "include",
  };

  if (config.method === "POST" && !config.body && !isFormData) {
    config.body = JSON.stringify({});
  }

  const response = await fetch(url, config);
  


  // 🔁 Handle token refresh once
  if (response.status === 401 && !retrying) {
    console.debug(`[API] 401 Unauthorized for ${endpoint}. Attempting check...`);

    // 1. Check if token already changed in background (e.g. concurrent request refreshed it)
    const latestToken = getStoredToken();
    if (latestToken && latestToken !== effectiveToken) {
      console.debug("[API] Token already refreshed by concurrent request, retrying...");
      return apiFetch(endpoint, latestToken, options, true);
    }

    // 2. Check if it's a "Session Revoked" error (SSA kick)
    const clone = response.clone();
    try {
      const errorBody = await clone.json();
      const errorCode = errorBody?.error_code || errorBody?.detail?.error_code;
      console.debug(`[API] Error code from 401: ${errorCode || 'none'}`);



      if (errorCode === 'SESSION_REVOKED') {
        console.warn("[API] SESSION_REVOKED detected. Terminating session.");
        // Decode the current token to extract sid so the modal can
        // verify the revocation targets THIS specific tab/session.
        let currentSid = null;
        try {
          const stored = getStoredToken();
          if (stored) {
            const payload = JSON.parse(atob(stored.split('.')[1]));
            currentSid = payload?.sid || null;
          }
        } catch { /* ignore decode errors */ }
        window.dispatchEvent(new CustomEvent('session-revoked', { detail: { sid: currentSid } }));
        return Promise.reject(new Error("Session terminated from another device."));
      }

      // Session expired / not found on the server — force immediate logout
      const isNotFound = errorCode === 'SESSION_NOT_FOUND' || errorBody?.detail?.error_code === 'SESSION_NOT_FOUND';
      const isIdleExpired = errorCode === 'SESSION_IDLE_EXPIRED' || errorBody?.detail?.error_code === 'SESSION_IDLE_EXPIRED';
      
      if (isNotFound || isIdleExpired) {
        console.warn(`[API] ${isIdleExpired ? 'SESSION_IDLE_EXPIRED' : 'SESSION_NOT_FOUND'} detected. Session has expired — logging out.`);
        clearTokens();
        window.dispatchEvent(new CustomEvent('session-expired'));
        return Promise.reject(new Error("Session expired. Please log in again."));
      }
    } catch (e) {
      console.debug("[API] 401 response body not JSON or parse error. Proceeding to refresh.");
    }

    // 3. Perform refresh
    const rfToken = getRefreshToken();
    if (!rfToken) {
      console.error("[API] 401 with no refresh token available. Logging out.");
      clearTokens();
      window.dispatchEvent(new CustomEvent('session-expired'));
      return response;
    }

    console.info("[API] Starting silent refresh...");
    const newToken = await refreshAccessToken();

    if (newToken) {
      console.info("[API] Silent refresh successful. Retrying original request.");
      return apiFetch(endpoint, newToken, options, true);
    }
    
    console.error("[API] Silent refresh failed. Forcing logout.");
    clearTokens();
    window.dispatchEvent(new CustomEvent('session-expired'));
    return response;
  }

  // ❌ Error handling (FastAPI-friendly)
  if (!response.ok) {
    if (response.status === 401 && retrying) {
      console.error("[API] Retried request still returned 401. Forcing logout.");
      clearTokens();
      window.dispatchEvent(new CustomEvent('session-expired'));
      return Promise.reject(new Error("Session expired. Please log in again."));
    }

    const text = await response.text();
    try {
      const parsed = JSON.parse(text);
      // --- MFA Check Integration ---
      if (response.status === 403 && parsed.mfa_required) {
        return parsed;
      }

      // --- Insufficient Credits Check ---
      if (response.status === 402) {
        console.warn("[API] 402 Payment Required detected. Triggering insufficient credits dialog.");
        window.dispatchEvent(new CustomEvent('insufficient-credits'));
        throw new Error("Insufficient credits. Please top up your wallet.");
      }



      // --- Password Expiry Integration ---
      if (response.status === 403 && (parsed.detail === "PASSWORD_EXPIRED" || parsed.message === "PASSWORD_EXPIRED")) {
        console.warn("[API] PASSWORD_EXPIRED detected. Triggering re-auth flow.");
        window.dispatchEvent(new CustomEvent('password-expired'));
        throw new Error("PASSWORD_EXPIRED");
      }

      // Prioritize common error fields
      let msg = parsed.detail || parsed.message || parsed.error;

      // If msg is still an object/array, stringify it pretty
      if (typeof msg === 'object' && msg !== null) {
        if (Array.isArray(msg)) {
          msg = msg.map(e => e.msg || JSON.stringify(e)).join("; ");
        } else {
          msg = JSON.stringify(msg);
        }
      }

      // Fallback if no specific field found
      if (!msg) {
        msg = JSON.stringify(parsed);
      }

      throw new Error(msg);
    } catch (e) {
      // Re-throw if we manually threw above
      if (e.message && e.message !== text) throw e;
      throw new Error(text || response.statusText);
    }
  }

  // 204 No Content
  if (response.status === 204) return null;

  const body = await response.text();
  if (!body) return null;

  const contentType = response.headers.get("content-type") || "";
  if (
    contentType.includes("text/plain") ||
    contentType.includes("text/markdown")
  ) {
    return body;
  }

  try {
    const parsedData = JSON.parse(body);
    
    // --- Global Job Failure Interceptor ---
    // If the API returns a 200 OK but the payload indicates a credit failure (e.g. from a background job), trigger the dialog
    if (parsedData && typeof parsedData === 'object' && !Array.isArray(parsedData)) {
       const status = parsedData.status || parsedData.state || parsedData.refresh_status;
       const errorMsg = typeof parsedData.error_message === 'string' ? parsedData.error_message : 
                        typeof parsedData.error === 'string' ? parsedData.error : 
                        typeof parsedData.message === 'string' ? parsedData.message : 
                        typeof parsedData.last_error === 'string' ? parsedData.last_error : "";
       
       const errorLower = errorMsg.toLowerCase();
       if (
           (status === 'failed' || status === 'FAILED' || status === 'error' || status === 'ERROR' || parsedData.code === 'INSUFFICIENT_CREDITS') &&
           (errorLower.includes('credit balance is too low') || errorLower.includes('insufficient credits') || parsedData.code === 'INSUFFICIENT_CREDITS')
       ) {
           console.warn("[API] Job failed due to insufficient credits. Triggering dialog.");
           window.dispatchEvent(new CustomEvent('insufficient-credits'));
       }
    }
    
    return parsedData;
  } catch {
    return body;
  }
}

export async function apiFetchBlob(endpoint, token = null, options = {}, retrying = false) {
  let effectiveToken = token || getStoredToken();
  const safeEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  const url = `${NORMALIZED_API_BASE}${safeEndpoint}`;
  const isFormData = options.body instanceof FormData;

  const headers = {
    ...(!isFormData && { "Content-Type": "application/json" }),
    ...(effectiveToken && { Authorization: `Bearer ${effectiveToken}` }),
    "X-Timezone": getActiveTimezone(),
    ...options.headers,
  };

  const config = {
    ...options,
    headers,
    credentials: "include",
  };

  if (config.method === "POST" && !config.body && !isFormData) {
    config.body = JSON.stringify({});
  }

  const response = await fetch(url, config);

  if (response.status === 401 && !retrying) {
    const latestToken = getStoredToken();
    if (latestToken && latestToken !== effectiveToken) {
      return apiFetchBlob(endpoint, latestToken, options, true);
    }
    const newToken = await refreshAccessToken();
    if (newToken) {
      return apiFetchBlob(endpoint, newToken, options, true);
    }
  }

  if (!response.ok) {
    const text = await response.text();
    try {
      const parsed = JSON.parse(text);
      if (response.status === 402) {
        window.dispatchEvent(new CustomEvent('insufficient-credits'));
        throw new Error("Insufficient credits.");
      }
      const msg = parsed.detail || parsed.message || parsed.error || text;
      throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } catch (e) {
      if (e.message && e.message !== text) throw e;
      throw new Error(text || response.statusText);
    }
  }

  const blob = await response.blob();
  const fileNameMatch = response.headers.get("content-disposition")?.match(/filename="?([^"]+)"?/i);
  return {
    blob,
    filename: fileNameMatch?.[1] || null,
    contentType: response.headers.get("content-type") || "",
  };
}

// ------------------------------------------------------------
// Convenience helpers
// ------------------------------------------------------------
export const apiGet = (endpoint, token) =>
  apiFetch(endpoint, token, { method: "GET" });

export const apiPost = (endpoint, token, data) =>
  apiFetch(endpoint, token, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const apiPut = (endpoint, token, data) =>
  apiFetch(endpoint, token, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const apiPatch = (endpoint, token, data) =>
  apiFetch(endpoint, token, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

export const apiDelete = (endpoint, token) =>
  apiFetch(endpoint, token, { method: "DELETE" });

// ------------------------------------------------------------
// Interactive Runner fetch (port 8001 — independent process)
// Use this for dashboard data/parquet queries so they run on
// the interactive runner, not the main API queue.
// ------------------------------------------------------------
const NORMALIZED_INTERACTIVE_BASE = (window.location.protocol === 'https:')
  ? INTERACTIVE_BASE_URL.trim().replace(/\/+$/, '').replace(/^http:\/\//i, 'https://')
  : INTERACTIVE_BASE_URL.trim().replace(/\/+$/, '');

export async function interactiveFetch(endpoint, token = null, options = {}) {
  let effectiveToken = token || getStoredToken();

  const safeEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  const url = `${NORMALIZED_INTERACTIVE_BASE}${safeEndpoint}`;

  const isFormData = options.body instanceof FormData;

  const headers = {
    ...(!isFormData && { "Content-Type": "application/json" }),
    ...(effectiveToken && { Authorization: `Bearer ${effectiveToken}` }),
    ...options.headers,
  };

  const config = { ...options, headers };

  if (config.method === "POST" && !config.body) {
    config.body = JSON.stringify({});
  }

  const response = await fetch(url, config);

  if (!response.ok) {
    const text = await response.text();
    try {
      const parsed = JSON.parse(text);
      if (response.status === 402) {
        window.dispatchEvent(new CustomEvent('insufficient-credits'));
        throw new Error("Insufficient credits.");
      }
      let msg = parsed.detail || parsed.message || parsed.error;
      if (typeof msg === 'object' && msg !== null) {
        msg = Array.isArray(msg) ? msg.map(e => e.msg || JSON.stringify(e)).join("; ") : JSON.stringify(msg);
      }
      throw new Error(msg || JSON.stringify(parsed));
    } catch (e) {
      if (e.message && e.message !== text) throw e;
      throw new Error(text || response.statusText);
    }
  }

  if (response.status === 204) return null;
  const body = await response.text();
  if (!body) return null;

  try {
    const parsedData = JSON.parse(body);
    
    // --- Global Job Failure Interceptor ---
    if (parsedData && typeof parsedData === 'object' && !Array.isArray(parsedData)) {
       const status = parsedData.status || parsedData.state || parsedData.refresh_status;
       const errorMsg = typeof parsedData.error_message === 'string' ? parsedData.error_message : 
                        typeof parsedData.error === 'string' ? parsedData.error : 
                        typeof parsedData.message === 'string' ? parsedData.message : 
                        typeof parsedData.last_error === 'string' ? parsedData.last_error : "";
       
       const errorLower = errorMsg.toLowerCase();
       if (
           (status === 'failed' || status === 'FAILED' || status === 'error' || status === 'ERROR' || parsedData.code === 'INSUFFICIENT_CREDITS') &&
           (errorLower.includes('credit balance is too low') || errorLower.includes('insufficient credits') || parsedData.code === 'INSUFFICIENT_CREDITS')
       ) {
           console.warn("[API] Job failed due to insufficient credits. Triggering dialog.");
           window.dispatchEvent(new CustomEvent('insufficient-credits'));
       }
    }

    return parsedData;
  } catch {
    return body;
  }
}



// ------------------------------------------------------------
// Feature / Plan / Support APIs
// ------------------------------------------------------------
export const getFeatures = (token) =>
  apiGet("/plans/features", token);

export const createFeature = (token, data) =>
  apiPost("/plans/features", token, data);

export const updateFeature = (token, featureId, data) =>
  apiPut(`/plans/features/${featureId}`, token, data);

export const deleteFeature = (token, featureId) =>
  apiDelete(`/plans/features/${featureId}`, token);

export const getPlanFeatures = (token, planId) =>
  apiGet(`/plans/${planId}/features`, token);

export const addPlanFeature = (token, data) =>
  apiPost(`/plans/${data.planId}/features`, token, data);

export const updatePlanFeature = (token, mappingId, data) =>
  apiPut(`/plans/plan-feature/${mappingId}`, token, data);

export const deletePlanFeature = (token, mappingId) =>
  apiDelete(`/plans/plan-feature/${mappingId}`, token);

export const getSupportTiers = (token) =>
  apiGet("/plans/support-tiers", token);

export const createSupportTier = (token, data) =>
  apiPost("/plans/support-tiers", token, data);

export const updateSupportTier = (token, tierId, data) =>
  apiPut(`/plans/support-tiers/${tierId}`, token, data);

export const deleteSupportTier = (token, tierId) =>
  apiDelete(`/plans/support-tiers/${tierId}`, token);

export const getPlanSupport = (token, planId) =>
  apiGet(`/plans/${planId}/support`, token);

export const assignSupportToPlan = (token, data) =>
  apiPost(`/plans/${data.planId}/support`, token, data);

export const removePlanSupport = (token, mappingId) =>
  apiDelete(`/plans/plan-support/${mappingId}`, token);

// ------------------------------------------------------------
// Tenant onboarding
// ------------------------------------------------------------
export const getOnboardingFull = (tenantId, token) =>
  apiGet(`/tenant-onboarding/${tenantId}/full`, token);

export const getOnboardingStatus = (tenantId, token) =>
  apiGet(`/tenant-onboarding/${tenantId}/status`, token);

export const saveAuthConfig = (tenantId, token, data) =>
  apiPost(`/tenant-onboarding/${tenantId}/auth`, token, data);

export const saveDbConfig = (tenantId, token, data) =>
  apiPost(`/tenant-onboarding/${tenantId}/db`, token, data);

export const saveFeatureConfig = (tenantId, token, data) =>
  apiPost(`/tenant-onboarding/${tenantId}/features`, token, data);

export const saveBrandingConfig = (tenantId, token, data) =>
  apiPost(`/tenant-onboarding/${tenantId}/branding`, token, data);

export const updateTenantBranding = (tenantId, token, formData) =>
  apiFetch(`/tenants/${tenantId}/branding`, token, {
    method: "POST",
    body: formData,
  });

export const uploadBrandingAsset = (tenantId, token, assetType, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return apiFetch(`/tenant-onboarding/${tenantId}/branding/upload?asset_type=${assetType}`, token, {
    method: "POST",
    body: formData,
  });
};

export const validateTenant = (tenantId, token) =>
  apiPost(`/tenant-onboarding/${tenantId}/validate`, token, {});

export const saveQuotaConfig = (tenantId, token, data) =>
  apiPost(`/tenant-onboarding/${tenantId}/quotas`, token, data);

// ------------------------------------------------------------
// Password Reset
// ------------------------------------------------------------
export const forgotPassword = (email) =>
  apiPost("/auth/forgot-password", null, { email });

export const resetPassword = (token, new_password) =>
  apiPost("/auth/reset-password", null, { token, new_password });

export const getTenantBranding = (token) =>
  apiGet("/tenant/branding", token);
